<template>
  <v-stepper v-model="currentStepPosition" editable>
    <v-stepper-header>
      <template v-for="step in stepperData.steps" :key="`${step.componentName}-step`">
        <v-stepper-item
          :complete="isStepComplete(step)"
          :editable="step.isClickable"
          :value="step.order"
          edit-icon="mdi-pencil"
          :color="isStepComplete(step) ? 'success' : 'grey'"
          @click="step.isClickable && handleStepSelect(step.order)"
        >
          {{ $t(step.translationKey) }}
        </v-stepper-item>

        <v-divider v-if="step.order < stepperData.steps.length" :key="step.order" />
      </template>
    </v-stepper-header>
    <v-stepper-window>
      <v-stepper-window-item
        v-for="step in stepperData.steps"
        :key="`${step.componentName}Content`"
        :value="step.order"
        selected-class="h-100"
      >
        <template v-if="activeStep.order === step.order">
          <component
            :is="activeStep.componentName"
            :ref="activeStep.componentName"
            :extract-type="extractType"
          />

          <!-- We hardcode the height of this row to ensure we fit page action buttons from component too, as they are bigger -->
          <div class="row stepper-actions align-content-center">
            <v-btn variant="text" :disabled="isPreviousDisabled" @click="handlePreviousStep">
              {{ $t('actions.previous') }}
            </v-btn>
            <v-btn :disabled="isNextDisabled" color="primary" @click="handleNextStep">
              {{ $t('actions.next') }}
            </v-btn>
          </div>
        </template>
      </v-stepper-window-item>
    </v-stepper-window>
  </v-stepper>
  <UnsavedDataModal
    ref="unsavedDataModal"
    :value="isUnsavedDataModalOpen"
    @cancel="closeUnsavedDataModal"
    @confirm="confirmUnsavedDataModal"
  />
</template>

<script>
import { mapState } from 'pinia';
import { size, get, reduce, find, cloneDeep, orderBy, last, flatten, values, some } from 'lodash';
import sectionStatuses from '@enums/section-status';
import { usePermission } from '@/composables/use-permission';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useScenariosStore } from '@/stores/scenarios';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },
  props: {
    stepData: {
      type: Array,
      required: true,
    },
    // If using this stepper from compound series selector
    fromSeries: {
      type: Boolean,
      required: false,
      default: false,
    },
    // get current status from series instead of scenario workflow
    seriesStatuses: {
      type: Object,
      required: false,
      default: null,
    },
    defaultToLastStepIfComplete: {
      type: Boolean,
      required: false,
      default: false,
    },
    extractType: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup() {
    const { canEditStoreExecutionPlanning } = usePermission();
    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      beforeNavWithUnsavedData,
    } = useUnsavedDataWarning();
    return {
      canEditStoreExecutionPlanning,
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      beforeNavWithUnsavedData,
    };
  },
  data() {
    return { currentStepPosition: null };
  },

  computed: {
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage']),
    ...mapState(useScenariosStore, ['selectedScenarioStatus']),

    hasDataChanges() {
      const components = flatten(values(this.$refs));
      return some(components.map(v => v.hasDataChanges));
    },

    stepperData() {
      return reduce(
        cloneDeep(this.stepData),
        (acc, step, index) => {
          if (this.fromSeries) {
            // Retrieve the current status for step from series workflow
            step.status = get(this.seriesStatuses, [step.statusSection, 'status'], null);
          } else {
            // Retrieve the current status for step from scenario workflow
            step.status = get(
              this.selectedScenarioStatus,
              [step.statusSection, step.statusSubSection, 'status'],
              null
            );
          }

          /* Allow step to be clickable if previous step is complete. First step should always be clickable.
             If previous step is not complete, fall back to isClickable value in stepData  */
          const currentClickState = this.stepData[index].isClickable;
          step.isClickable =
            acc.previousStep.status === sectionStatuses.complete || !!currentClickState;

          acc.steps.push(step);
          acc.previousStep = step;
          return acc;
        },
        { steps: [], previousStep: { status: sectionStatuses.complete } }
      );
    },

    activeStep() {
      return this.stepperData.steps[this.currentStepPosition - 1];
    },

    isFirstStep() {
      return this.currentStepPosition === 1;
    },

    isLastStep() {
      return this.currentStepPosition === size(this.stepperData.steps);
    },

    isNextDisabled() {
      return this.isLastStep || this.activeStep.status !== sectionStatuses.complete;
    },

    isEditingDisabled() {
      return !this.canEditStoreExecutionPlanning;
    },

    isPreviousDisabled() {
      return (
        this.isFirstStep ||
        this.stepperData.steps[this.currentStepPosition - 2].status !== sectionStatuses.complete
      );
    },
  },

  created() {
    const orderedSteps = orderBy(this.stepperData.steps, 'order');
    const lastStep = last(orderedSteps);

    // If the last step is in-progress or complete, default to that step on page load
    if (this.defaultToLastStepIfComplete && lastStep.status !== sectionStatuses.notStarted) {
      this.currentStepPosition = lastStep.order;
    } else {
      // Set default to first incomplete step
      const stepInProgress = find(orderedSteps, s => s.status !== sectionStatuses.complete);
      this.currentStepPosition = stepInProgress.order;
    }
  },

  methods: {
    isStepComplete(step) {
      return step.status === sectionStatuses.complete && step !== this.activeStep;
    },

    goToStep(stepOrder) {
      this.currentStepPosition = stepOrder;
    },

    handleNextStep() {
      if (!this.isNextDisabled) {
        this.handleStepSelect(this.currentStepPosition + 1);
      }
    },

    handlePreviousStep() {
      if (!this.isFirstStep) {
        this.handleStepSelect(this.currentStepPosition - 1);
      }
    },

    handleStepSelect(stepOrder) {
      this.beforeNavWithUnsavedData(() => {
        this.goToStep(stepOrder);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.stepper-actions {
  height: 33px; /* To accommodate action buttons from step components */
}
</style>
